import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Renders an AI answer's GitHub-flavored markdown inside the NLQ message body
 * (SCRUM-83, plan D4). Styling ports the prototype's `.nlq-msg-ai__body`
 * element rules (strong / table / th / td / paragraph spacing) onto a
 * react-markdown component map.
 *
 * SECURITY (plan RT1): rehype-raw is deliberately NOT enabled — any raw HTML
 * in the model output renders as inert text, never as DOM. The NlqAnswerV1
 * prompt instructs GFM-only output; this component is the enforcement layer.
 *
 * RESPONSIVE: tables get horizontal scroll within their own container
 * (overflow-x-auto wrapper) per the frontend responsive rule — the prototype
 * ships no @media rules for the NLQ page.
 */
export function NlqMarkdown({ markdown }: { markdown: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => <p className="my-2 first:mt-0 last:mb-0">{children}</p>,
        strong: ({ children }) => <strong className="text-text-0 font-semibold">{children}</strong>,
        ul: ({ children }) => (
          <ul className="my-2 list-disc space-y-1 pl-5 first:mt-0 last:mb-0">{children}</ul>
        ),
        ol: ({ children }) => (
          <ol className="my-2 list-decimal space-y-1 pl-5 first:mt-0 last:mb-0">{children}</ol>
        ),
        table: ({ children }) => (
          <div className="overflow-x-auto">
            <table className="my-3 w-full border-collapse text-[0.8125rem]">{children}</table>
          </div>
        ),
        th: ({ children }) => (
          <th className="border-border-1 text-text-0 border-b-2 px-2 py-1.5 text-left font-mono text-xs font-semibold">
            {children}
          </th>
        ),
        td: ({ children }) => (
          <td className="border-border-0 text-text-1 border-b px-2 py-1.5">{children}</td>
        ),
        // Links are stripped to text: the model is grounded in a numeric
        // snapshot and has no legitimate URLs to cite — any link it invents
        // would be a hallucination (and a phishing surface in a banking UI).
        a: ({ children }) => <span>{children}</span>,
      }}
    >
      {markdown}
    </ReactMarkdown>
  );
}
